import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { colors, fonts, radii, shadows, breakpoints } from './tokens';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { MicIcon, CameraIcon, CogIcon, EyeSlashIcon, EyeIcon } from './icons';
import { useVideo } from '../context/videoContext';
import { describeMediaError } from '../helpers/video/media';
import { LocalVideoTile, RemoteVideoTile } from './VideoTile';

// ---------------------------------------------------------------------------------------------
// Device settings popover (the cog): camera / microphone pickers.
// ---------------------------------------------------------------------------------------------

const Pop = styled.div`
  position: absolute;
  z-index: 60;
  ${({ placement }) => (placement === 'top' ? 'bottom: calc(100% + 8px);' : 'top: calc(100% + 8px);')}
  left: 0;
  width: min(300px, calc(100vw - 32px));
  padding: 14px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid ${colors.slate[200]};
  border-radius: ${radii.dialog}px;
  box-shadow: ${shadows.menu};
  font-family: ${fonts.family};
  display: flex;
  flex-direction: column;
  gap: 12px;
  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
    color: ${colors.slate[600]};
  }
  select {
    height: 40px;
    padding: 0 8px;
    border: 1px solid ${colors.slate[200]};
    border-radius: ${radii.input}px;
    font: 400 14px ${fonts.family};
    color: ${colors.text};
    background: #fff;
  }
  p {
    margin: 0;
    font-size: 12px;
    color: ${colors.textMuted};
  }
  .err {
    color: ${colors.error[600]};
  }
`;

export const DeviceSettings = ({ placement = 'bottom', onClose }) => {
  const { devices = { videoInputs: [], audioInputs: [] }, selectedDevices = {}, switchDevice, deviceError, audioError, localTracks } = useVideo() || {};
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && !e.target.closest?.('[data-device-settings-toggle]') && onClose?.();
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const err = deviceError || (audioError && localTracks?.video ? audioError : null);
  const none = !devices.videoInputs.length && !devices.audioInputs.length;
  return (
    <Pop ref={ref} placement={placement} data-testid='device-panel' role='dialog' aria-label='Camera and microphone settings'>
      {none && <p>No cameras or microphones found. Allow access in your browser to choose a device.</p>}
      {devices.videoInputs.length > 0 && (
        <label>
          Camera
          <select value={selectedDevices.video || ''} onChange={(e) => switchDevice?.('video', e.target.value || null)}>
            <option value=''>Default</option>
            {devices.videoInputs.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {devices.audioInputs.length > 0 && (
        <label>
          Microphone
          <select value={selectedDevices.audio || ''} onChange={(e) => switchDevice?.('audio', e.target.value || null)}>
            <option value=''>Default</option>
            {devices.audioInputs.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {err && <p className='err'>{describeMediaError(err)}</p>}
    </Pop>
  );
};

// ---------------------------------------------------------------------------------------------
// VideoControls - [Hide Video Feed] [mic] [cam] ........ [cog]
// ---------------------------------------------------------------------------------------------

const ControlsRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: ${({ column }) => (column ? 'wrap' : 'nowrap')};
  font-family: ${fonts.family};
  .divider {
    flex: 1;
    height: 1px;
    min-width: 16px;
    background: ${colors.slate[200]};
  }
`;

const Anchor = styled.div`
  position: relative;
  display: inline-flex;
  margin-left: ${({ pushRight }) => (pushRight ? 'auto' : 0)};
`;

/**
 * VideoControls
 * @param hidden / onToggleHidden   render the "Hide Video Feed" / "Show Video Feed" toggle
 * @param settingsAlign             'inline' (next to cam, strip) | 'right' (pushed right, lobby)
 * @param divider                   draw the thin rule after the buttons (portrait strip)
 * @param column                    wrap (landscape column)
 */
export const VideoControls = ({ hidden, onToggleHidden, settingsAlign = 'inline', divider = false, column = false, popPlacement = 'top' }) => {
  const video = useVideo() || {};
  const [showDevices, setShowDevices] = useState(false);
  const micOff = !video.localTracks?.audio || video.isMicEnabled === false;
  const camOff = !video.localTracks?.video || video.isCameraEnabled === false;
  return (
    <ControlsRow column={column} data-testid='video-controls'>
      {onToggleHidden && (
        <Button
          variant='secondary'
          size='md'
          onClick={onToggleHidden}
          iconLeft={hidden ? <EyeIcon size={18} /> : <EyeSlashIcon size={18} />}
          style={{ borderColor: colors.slate[500], color: colors.slate[600], height: 44 }}
          aria-pressed={Boolean(hidden)}
        >
          {hidden ? 'Show Video Feed' : 'Hide Video Feed'}
        </Button>
      )}
      <IconButton label={micOff ? 'Turn microphone on' : 'Mute microphone'} variant={micOff ? 'danger' : 'filled'} pressed={micOff} onClick={video.toggleMic} disabled={!video.toggleMic}>
        <MicIcon off={micOff} size={20} />
      </IconButton>
      <IconButton label={camOff ? 'Turn camera on' : 'Turn camera off'} variant={camOff ? 'danger' : 'filled'} pressed={camOff} onClick={video.toggleCamera} disabled={!video.toggleCamera}>
        <CameraIcon off={camOff} size={20} />
      </IconButton>
      <Anchor pushRight={settingsAlign === 'right'}>
        <IconButton label='Camera and microphone settings' variant='outline' aria-expanded={showDevices} data-device-settings-toggle onClick={() => setShowDevices((s) => !s)}>
          <CogIcon size={20} />
        </IconButton>
        {showDevices && <DeviceSettings placement={popPlacement} onClose={() => setShowDevices(false)} />}
      </Anchor>
      {divider && <span className='divider' aria-hidden='true' />}
    </ControlsRow>
  );
};

// ---------------------------------------------------------------------------------------------
// VideoStrip - controls + two tiles. orientation 'row' (portrait: under the panel) or 'column'
// (landscape: left column). Hidden = tiles parked off-screen but still mounted, so the local
// <video> (face-api / corners-fixation read it) keeps playing.
// ---------------------------------------------------------------------------------------------

const StripRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  ${({ orientation }) => (orientation === 'column' ? 'height: 100%;' : '')}
`;

const Tiles = styled.div`
  display: grid;
  gap: ${({ orientation }) => (orientation === 'column' ? 8 : 12)}px;
  ${({ orientation, tileHeight }) =>
    orientation === 'column'
      ? `grid-template-rows: repeat(2, minmax(0, 1fr)); flex: 1; min-height: 0;`
      : `grid-template-columns: repeat(2, minmax(0, 1fr)); height: ${tileHeight};`}
  @media (max-width: ${breakpoints.phone - 1}px) {
    gap: 12px;
  }
  &[data-hidden='true'] {
    position: fixed;
    left: 0;
    bottom: 0;
    width: 4px;
    height: 4px;
    opacity: 0.01;
    pointer-events: none;
    overflow: hidden;
    z-index: -1;
  }
`;

/**
 * VideoStrip
 * @param localLabel / localName     e.g. "Clinician", "Della Craig"
 * @param remoteLabel / remoteName   e.g. "Patient", "Villiams da Silva"
 * @param order                      'local-first' (default) | 'remote-first'
 * @param hidden / onToggleHidden    video feed toggle (tiles stay mounted when hidden)
 * @param orientation                'row' | 'column'
 * @param tileHeight                 CSS height of the row of tiles (row orientation)
 */
export const VideoStrip = ({
  localLabel,
  localName,
  remoteLabel,
  remoteName,
  order = 'local-first',
  hidden = false,
  onToggleHidden,
  orientation = 'row',
  tileHeight = 'clamp(150px, 22vh, 252px)',
  localTileProps,
  remoteTileProps,
}) => {
  const local = <LocalVideoTile key='local' roleLabel={localLabel} name={localName} {...localTileProps} />;
  const remote = <RemoteVideoTile key='remote' roleLabel={remoteLabel} name={remoteName} {...remoteTileProps} />;
  return (
    <StripRoot orientation={orientation} data-testid='video-strip'>
      <VideoControls hidden={hidden} onToggleHidden={onToggleHidden} divider={orientation === 'row'} column={orientation === 'column'} popPlacement={orientation === 'row' ? 'top' : 'bottom'} />
      <Tiles orientation={orientation} tileHeight={tileHeight} data-hidden={hidden ? 'true' : 'false'} aria-hidden={hidden || undefined}>
        {order === 'remote-first' ? [remote, local] : [local, remote]}
      </Tiles>
    </StripRoot>
  );
};

export default VideoStrip;
